/**
 * Campaign content: every text of the flow on the wall and on the phone, and the timing of the
 * end sequence on the wall. One place for both game editions and the controller.
 *
 * The CTA link itself comes from the server (CTA_URL) – exchangeable without a build.
 */
import { MAX_BASE_SUBSIDY_EUR, formatEuro, type Score } from '../shared/scoring.js';

export interface ResultText {
  /** from this many coins on (the highest matching tier wins) */
  minCoins: number;
  title: string;
  text: string;
}

/** No line break between a number and its unit ("25 %", "540 €") in running text. */
export function noBreak(text: string): string {
  return text.replace(/ (?=[€%])/g, '\u00a0');
}

export const CAMPAIGN = {
  /** waiting mode on the wall: the screens appear one after the other, the demo alone in between */
  idle: {
    invite: { title: 'Jetzt mitspielen!', sub: 'QR-Code scannen und das Spiel mit dem Handy steuern.' },
    leaderboard: { title: 'Highscore', empty: 'Noch kein Highscore\n– hol dir Platz\u00a01!' },
    /** the 65 % message (Allianz Vorsorge Index 2026, IPSOS, n = 1.000), one screen per part, in white */
    story: ['65 Prozent der Deutschen sparen für den Ruhestand.', 'Entdecke, was der Staat dir dazugibt.'],
    /** after the story, in white with the arrow to the QR code */
    call: 'Mit dem Allianz Altersvorsorgedepot!',
    /** seconds per screen: invitation, top 5, each part of the story, the call – and the demo alone before each of them */
    durationsSec: { invite: 8, leaderboard: 8, story: 6, call: 6, pause: 3 },
  },
  /** `sec` = seconds left to press START */
  ready: { title: 'Du bist dran!', sub: (sec: number) => (sec > 0 ? `Drücke START auf deinem Handy\u00a0(${sec}\u00a0s).` : 'Drücke START auf deinem Handy.') },
  /** end screen: texts by result (with confetti), then the call to action */
  result: {
    tiers: [
      { minCoins: 0, title: 'Da geht noch mehr!', text: 'Probier es gleich noch mal.' },
      { minCoins: 1, title: 'Guter Anfang!', text: 'Jede Münze zählt – und der Staat legt etwas drauf.' },
      { minCoins: 12, title: 'Stark gespart!', text: '' },
      { minCoins: 24, title: 'Richtig stark!', text: 'Fast alles mitgenommen.' },
    ] as ResultText[],
    max: { title: 'Maximum erreicht!', text: 'Volles Depot und die volle staatliche Zulage.' },
  },
  /** at the end of a round, between result and call to action – one screen per part (none: straight to the call to action) */
  story: [] as string[],
  /** the amounts of a result: own contribution and subsidy (wall, phone, HUD) */
  amounts: { own: 'Altersvorsorgedepot', subsidy: 'Staatliche Zulage', specialsOf: 'davon' },
  /** the same as labels of the HUD panel on the facade, two lines each */
  hudLabels: { own: 'Altersvorsorge-\nDepot', subsidy: 'Staatliche\nZulage' },
  cta: {
    /** call to action at the end of a round in the mobile edition (the game on the phone itself) */
    headline: 'Das Allianz Altersvorsorgedepot.',
    /** the same on the wall, in white – the link is on the player's phone */
    wall: 'Mehr Infos auf deinem Handy.',
    /** button on the phone */
    button: 'Jetzt mehr zum Allianz Altersvorsorgedepot erfahren',
  },
  /**
   * The controller on the phone. No technical words here (connection, server, session …) –
   * those only appear in the debug panel.
   */
  phone: {
    loading: 'Einen Moment …',
    /** the phone has not reached the game for a while */
    slow: 'Das dauert gerade länger als sonst. Ist dein Handy online?',
    /** banner while the phone reconnects (the screen below stays) */
    hold: 'Einen Moment – gleich geht’s weiter.',
    waiting: {
      kicker: 'Warteschlange',
      next: 'Du bist als Nächstes dran!',
      /** label below the own figure in the queue */
      you: 'Du',
      ahead: (n: number) => (n === 1 ? 'Person vor dir' : 'Personen vor dir'),
      hint: (sec: number) => `Wenn du dran bist, drücke START – du hast dafür ${sec} Sekunden. Lass diese Seite einfach geöffnet.`,
      /** came back after the place in the queue was given away */
      requeued: 'Du warst eine Weile weg – wir haben dich wieder angestellt.',
      offline: 'Das Spiel macht gerade eine kurze Pause.',
      howTo: { title: 'So geht’s:', steps: ['Schau auf die Wand.', 'Tippe auf deinem Handy, um zu springen.', 'Sammle Goldmünzen – die staatliche Zulage fliegt dir zu.'] },
      leave: 'Warteschlange verlassen',
    },
    ready: {
      sub: 'Drücke START – das Spiel beginnt auf der Wand.',
      button: 'Start',
      left: (sec: number) => `Noch ${sec} ${sec === 1 ? 'Sekunde' : 'Sekunden'}`,
      skip: 'Doch nicht spielen',
    },
    jumpHint: 'Schau auf die Wand und drücke,\num zu springen.',
    /** the wall is gone for a moment during the round */
    playPaused: 'Das Spiel ist kurz weg …',
    /** `next` = on to the call to action (the phone does not switch by itself) */
    result: { next: 'Weiter' },
    rank: (rank: number) => `Du bist im Highscore – Platz ${rank}!`,
    share: {
      button: 'Freunde zum Spielen einladen',
      copied: 'Link kopiert!',
      title: 'Später noch was vor?',
      text: 'Spiel Allianz Altersvorsorgedepot – jetzt auf dem Handy spielen:',
    },
    /** a turn that ended without a round: title, text and the button that goes with it */
    finished: {
      left: { title: 'Du hast die Warteschlange verlassen.', text: 'Doch Lust bekommen? Stell dich einfach wieder an.', action: 'Wieder anstellen' },
      ready_timeout: {
        title: 'Zu spät gestartet.',
        text: 'Du hast nicht rechtzeitig START gedrückt – dein Platz ist weg. Stell dich gern wieder an.',
        action: 'Wieder anstellen',
      },
      queue_full: { title: 'Gerade ist viel los.', text: 'Die Warteschlange ist voll. Versuch es gleich noch einmal.', action: 'Erneut versuchen' },
      aborted: { title: 'Deine Runde wurde unterbrochen.', text: 'Das tut uns leid! Stell dich gern wieder an.', action: 'Wieder anstellen' },
    },
    closed: {
      /** the facade only plays when it is dark */
      title: 'Die Fassade spielt erst im Dunkeln.',
      text: (when: string) => `Das Spiel startet ${when}. Bis dahin kannst du auf dem Handy üben.`,
      action: 'Auf dem Handy spielen',
      /** it has opened in the meantime */
      openTitle: 'Die Fassade ist an!',
      openText: 'Stell dich jetzt an – gleich bist du dran.',
      openAction: 'Jetzt anstellen',
    },
    /** the phone cannot (or no longer) take part */
    stopped: {
      noGame: { title: 'Willkommen!', text: 'Scanne den QR-Code an der Fassade, um mitzuspielen.' },
      unknownGame: { title: 'Dieser Code passt zu keinem Spiel.', text: 'Bitte scanne den QR-Code an der Fassade noch einmal.' },
      otherTab: { title: 'Du spielst in einem anderen Fenster.', text: 'Dieses Fenster kannst du schließen.' },
      idle: { title: 'Noch da?', text: 'Stell dich wieder an, wenn du spielen möchtest.', action: 'Wieder anstellen' },
      full: { title: 'Gerade ist sehr viel los.', text: 'Bitte versuch es in ein paar Minuten noch einmal.', action: 'Erneut versuchen' },
      update: { title: 'Es gibt eine neue Version.', text: 'Bitte lade die Seite neu.', action: 'Neu laden' },
      other: { title: 'Das hat leider nicht geklappt.', text: 'Bitte versuch es noch einmal.', action: 'Erneut versuchen' },
    },
  },
  /**
   * Conversion page after the result on the phone ("Weiter"), below the logo: header, the foxes, one sentence,
   * the call to action + invite friends. A round is one year of saving (the coins of one year, the subsidy per year).
   * Wording follows the mural ("bis zu 540 € Grundzulage").
   */
  ctaPage: {
    /** two lines, like the headline of the mural */
    hero: ['Level-up für', 'deine Rente'],
    /** one short sentence above the call to action */
    text: noBreak(`Du zahlst ein und der Staat legt drauf: bis\u00a0zu ${formatEuro(MAX_BASE_SUBSIDY_EUR)} jährliche Grundzulage.`),
    /** CTA_URL – the only button that leads away */
    cta: 'Jetzt mehr zum Allianz Altersvorsorgedepot erfahren',
  },
  /** timing of the end sequence on the wall (s after the end of the round): the parts of the story share the time from `storyAtSec` to `ctaAtSec` */
  endSequence: { storyAtSec: 5, ctaAtSec: 5 },
} as const;

/** End screen texts for a result. */
export function resultText(score: Pick<Score, 'coins' | 'max'>): ResultText {
  if (score.max) return { minCoins: score.coins, ...CAMPAIGN.result.max };
  let best: ResultText = CAMPAIGN.result.tiers[0];
  for (const t of CAMPAIGN.result.tiers) if (score.coins >= t.minCoins) best = t;
  return best;
}

/**
 * The end of a round in the game, over the whole window (game-core EndingContent): result with confetti → story →
 * call to action. `cta` = the call to action (headline, optional line below, colour of the headline),
 * `space` = shares of the window kept free at the top / bottom.
 */
export function campaignEnding(cta: { headline: string; hint?: string; color?: string }, space: { top?: number; bottom?: number } = {}) {
  return {
    // only the title above the amounts – the texts by result are not shown at the moment (wall and phone)
    result: (score: Score) => ({ title: resultText(score).title, text: '' }),
    story: CAMPAIGN.story,
    amounts: CAMPAIGN.amounts,
    cta,
    storyAtSec: CAMPAIGN.endSequence.storyAtSec,
    ctaAtSec: CAMPAIGN.endSequence.ctaAtSec,
    topSpace: space.top ?? 0,
    bottomSpace: space.bottom ?? 0,
  };
}

/** Opens the landing page in a new tab. Call from a click/pointerup handler (popup blockers). */
export function openCta(url: string): void {
  if (!url) {
    console.info('CTA: no campaign URL configured (CTA_URL on the server)');
    return;
  }
  window.open(url, '_blank', 'noopener');
}
